import {
  Directive,
  ElementRef,
  Input,
  TemplateRef,
  ViewContainerRef,
  EmbeddedViewRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  EventEmitter,
  Output,
  ChangeDetectorRef
} from '@angular/core';
import tippy, { Instance, Props } from 'tippy.js';

@Directive({
    selector: '[dianaTippy]',
    standalone: false
})
export class TippyDirective implements OnChanges, OnDestroy {
  @Input('dianaTippy') content!: string | TemplateRef<any>;

  @Input() tippyOptions: Partial<Props> = {
    allowHTML: true,
    appendTo: () => document.body,
    arrow: true,
    delay: [20, 5],
    interactive: true,
    maxWidth: 500,
    placement: 'auto',
    theme: 'light',
    trigger: 'mouseenter click'
  };

  @Input() maxWidth = 0;
  @Input() autoShowScroll = false;
  @Input() disable = false;

  @Output() tippyShown = new EventEmitter<Instance>();
  @Output() tippyHidden = new EventEmitter<Instance>();

  private tippy!: Instance<Props>;
  private contentContainer?: HTMLElement;
  private embeddedView?: EmbeddedViewRef<any>;
  private context = { isVisible: false };

  constructor(
    private el: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef // add this
  ) {
    this.loadTippy(this.el.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.content && this.tippy) {
      this.setContent(this.content);
    }

    if (changes.tippyOptions && this.tippy) {
      this.tippy.setProps(changes.tippyOptions.currentValue);
    }

    if (changes.disable && this.tippy) {
      if (this.disable) {
        this.tippy.disable();
      } else {
        this.tippy.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.tippy?.destroy();
    this.embeddedView?.destroy();
  }

  private setContent(content: string | TemplateRef<any>): void {
    if (typeof content === 'string') {
      this.tippy.setContent(content);
    } else if (content instanceof TemplateRef) {
      this.context.isVisible = false;
  
      // Destroy old view if any
      this.embeddedView?.destroy();
  
      this.embeddedView = content.createEmbeddedView(this.context);
      this.embeddedView.detectChanges();
  
      // Proper Angular way: insert into view container
      this.viewContainerRef.insert(this.embeddedView);
  
      // Create a container element for tippy
      this.contentContainer = document.createElement('div');
      this.embeddedView.rootNodes.forEach(node => {
        this.contentContainer!.appendChild(node);
      });
  
      this.tippy.setContent(this.contentContainer);
    }
  }

  private onShow(instance: Instance): void {
    const rect = instance.popper.getBoundingClientRect();
    const maxHeight = window.innerHeight - rect.top;

    if (this.autoShowScroll) {
      if (instance.popper.style.overflowY) {
        instance.popper.style.overflowY = '';
        instance.popper.style.overflowX = '';
      }
      const roundingTolerance = 2;
      const popperHeight = rect.height;
      if (
        popperHeight > maxHeight + roundingTolerance ||
        (!instance.popper.style.overflowY &&
          instance.popper.scrollHeight >
            instance.popper.offsetHeight + roundingTolerance)
      ) {
        instance.popper.style.overflowY = 'auto';
        instance.popper.style.overflowX = 'hidden';
      }
    }
    instance.popper.style.maxHeight = `${maxHeight}px`;

    // Update tooltip visibility in Angular context
    this.context.isVisible = true;
    this.embeddedView?.detectChanges();
    this.cdr.markForCheck();
  }

  private onHide(): void {
    this.embeddedView?.detectChanges();
    this.cdr.markForCheck();

  }

  private loadTippy(el: Element): void {
    const tippyOptions: Partial<Props> = {
      ...this.tippyOptions,
      onMount: instance => this.onShow(instance),
      onShown: instance => this.tippyShown.emit(instance),
      onHidden: instance => {
        this.onHide();
        this.tippyHidden.emit(instance);
      },
      maxWidth: this.maxWidth || this.tippyOptions.maxWidth
    };

    this.tippy = tippy(el, tippyOptions);
    this.setContent(this.content);
    this.cdr.markForCheck();

  }
}